import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './ProjectDashboard.module.css';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../../components/loader2';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import api from '../../api/api.js';
import showToast from '../../components/ShowToast';
import { Card, CardContent, CardHeader, CardTitle } from "../../_newprofiles/components/ui/Card";
import { Plus, Folder, FileText, MessageSquare, DollarSign } from "lucide-react";
import AddEditProjectModal from './components/AddEditProjectModal';
import CreateProjectModal from '../../components/Projects/CreateProjectModal';

const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [userId, setOwnerUserId] = useState(null);
  const [userType, setUserType] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    let uid = null;
    let uname = '';
    let uType = '';
    let uTypeFull = '';
    if (token) {
      try {
        const decodedToken = jwtDecode(token);

        uType = localStorage.getItem('userType') || '';
        uname = decodedToken.UserName || localStorage.getItem('loggedInUsername') || 'User';

        if (uType == "Home") {
          uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
          uTypeFull = "HomeUser";
        } else if (uType == "Business") {
          uid = decodedToken.nameid || localStorage.getItem('loggedInBusinessUserId');
          uTypeFull = "BusinessUser";
        }
      
      } catch (e) {
        console.warn("JWT decode failed, falling back to storage:", e);
        uname = localStorage.getItem('loggedInUsername') || 'User';
        uid = localStorage.getItem('loggedInHomeUserId');
      }
    } else {
      uname = localStorage.getItem('loggedInUsername') || '';
      uid = localStorage.getItem('loggedInHomeUserId');
    }

    if (uname) setUsername(uname);
    if (uid) setOwnerUserId(uid);
    if (uTypeFull) setUserType(uTypeFull);

    if (!uid) setError("No user identified. Please log in.");
  }, []);

  const fetchProjects = async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/Projects/getmyprojects?userId=${userId}&userType=${userType}`);
      const filteredProjects = response.data.filter(p => p.name && p.name.toLowerCase() !== 'general maintenance');
      setProjects(filteredProjects);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const openCreateProjectModal = () => {
    setIsCreateProjectModalOpen(true);
  };

  const closeCreateProjectModal = () => {
    setIsCreateProjectModalOpen(false);
  };

  const closeAddEditModal = () => {
    setSelectedProject(null);
    setIsAddEditModalOpen(false);
  };

  const handleSaveProject = async (projectData) => {
    // ... existing save logic ...
    closeAddEditModal();
    fetchProjects();
  };

  const stats = {
    totalProjects: projects.length,
    activeProjects: projects.filter((project) => project.status === "Planning" || project.status === "InProgress").length,
    totalJobs: projects.reduce((sum, project) => sum + project.jobCount, 0),
    activeJobs: 0,
    totalQuotes: 0,
    totalBudget: 0, 
  };

  return (
    <>
      <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'projects'} />
      <div className={styles.pageContainer}>
        
        {/* GOLD HEADER - Clean & Blue Text */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1>My Projects</h1>
            <button className={styles.btnGold} onClick={openCreateProjectModal}>
              <Plus size={18} />
              Create New Project
            </button>
          </div>
        </header>

        {/* Stats Cards - Clean White Style */}
        <div className={styles.statsContainer}>
          
          {/* Blue Card */}
          <div className={`${styles.statCard} ${styles.themeBlue}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div>
                  <p className={styles.statLabel}>Total Projects</p>
                  <p className={styles.statValue}>{stats.totalProjects}</p>
                  <p className={styles.statSubtitle}>{stats.activeProjects} active</p>
                </div>
                <div className={styles.iconWrapper}>
                    <Folder size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Purple Card */}
          <div className={`${styles.statCard} ${styles.themePurple}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div>
                  <p className={styles.statLabel}>Total Jobs</p>
                  <p className={styles.statValue}>{stats.totalJobs}</p>
                  <p className={styles.statSubtitle}>{stats.activeJobs} active</p>
                </div>
                <div className={styles.iconWrapper}>
                    <FileText size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Amber Card */}
          <div className={`${styles.statCard} ${styles.themeAmber}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div>
                  <p className={styles.statLabel}>Total Quotes</p>
                  <p className={styles.statValue}>{stats.totalQuotes}</p>
                  <p className={styles.statSubtitle}>Sent to Clients</p>
                </div>
                <div className={styles.iconWrapper}>
                    <MessageSquare size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Green Card */}
          <div className={`${styles.statCard} ${styles.themeGreen}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div>
                  <p className={styles.statLabel}>Total Spend</p>
                  <p className={styles.statValue}>${(stats.totalBudget / 1000).toFixed(0)}K</p>
                  <p className={styles.statSubtitle}>Across all jobs</p>
                </div>
                <div className={styles.iconWrapper}>
                    <DollarSign size={24} />
                </div>
              </div>
            </div>
          </div>

        </div>

        <main className={styles.mainContent}>
          {isLoading && <Loader2 />}
          {error && <p className={styles.errorState}>Error: {error}</p>}
          {!isLoading && !error && projects.length === 0 && (
            <div className={styles.emptyState}>
              <p>No projects yet. Click "Create New Project" to get started.</p>
            </div>
          )}
          {!isLoading && !error && projects.length > 0 && (
            <div className={styles.projectsGrid}>
              {projects.map((project) => (
                <Link to={`/project/${project.projectId}`} key={project.projectId} className={styles.projectCardLink}>
                  <Card className={styles.projectCard}>
                    <CardHeader>
                      <CardTitle className={styles.projectCardTitle}>{project.name}</CardTitle>
                    </CardHeader>
                    <CardContent style={{display:'flex', flexDirection:'column', flexGrow:1}}>
                      <p className={styles.projectDescription}>{project.description || "No description provided."}</p>
                      <div className={styles.projectMeta}>
                        <span className={styles.projectStatus}>{project.status}</span>
                        <span className={styles.projectJobCount}>{project.jobCount} Jobs</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>

      <AddEditProjectModal
        isOpen={isAddEditModalOpen}
        onClose={closeAddEditModal}
        onSave={handleSaveProject}
        project={selectedProject}
      />

      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={closeCreateProjectModal}
      />
    </>
  );
};

export default ProjectDashboard;